import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
export default function Home() {
  useEffect(() => {
    document.title = "Lifetime Creation Embroidery Design";
  }, []);
  const navigate = useNavigate();
  const [showCoursePopup, setShowCoursePopup] = useState(false);
  const [bannerData, setBannerData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentBannerIndex, setCurrentBannerIndex] = useState(0);
  const [designData, setDesignData] = useState([]);
  const [showWelcomePopup, setShowWelcomePopup] = useState(false);
  useEffect(() => {
    const offerShown = sessionStorage.getItem("offerPopupShown");

    if (!offerShown) {
      const timer = setTimeout(() => {
        setShowWelcomePopup(true);

        sessionStorage.setItem("offerPopupShown", "true");
      }, 800);

      return () => clearTimeout(timer);
    }
  }, []);
  // Preload banner images
  const preloadImages = useCallback((images) => {
    images.forEach((image) => {
      if (image?.Value) {
        const img = new Image();
        img.src = image.Value;
      }
    });
  }, []);

  useEffect(() => {
    const lastShown = localStorage.getItem("coursePopupDate");
    const now = new Date().getTime();

    const sevenDays = 7 * 24 * 60 * 60 * 1000;

    if (!lastShown || now - Number(lastShown) > sevenDays) {
      const timer = setTimeout(() => {
        setShowCoursePopup(true);

        // Save current time
        localStorage.setItem("coursePopupDate", now);
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, []);
  // Fetch both Banner + Design data
  useEffect(() => {
    const fetchAllData = async () => {
      try {
        setLoading(true);
        setError(null);

        /* ================= Banner API ================= */
        const bannerResponse = await fetch(
          "https://lifetimedesign.in/LtcJsonAPI.asmx/GetBannerList",
        );

        if (!bannerResponse.ok) {
          throw new Error("Banner fetch failed");
        }

        const bannerApiResponse = await bannerResponse.json();

        const bannerList =
          bannerApiResponse.success && bannerApiResponse.data
            ? bannerApiResponse.data
            : [];

        setBannerData(bannerList);
        preloadImages(bannerList);

        /* ================= Design API ================= */
        const designResponse = await fetch(
          "https://lifetimedesign.in/LtcJsonAPI.asmx/GetHomeDesigns",
        );

        if (!designResponse.ok) {
          throw new Error("Design fetch failed");
        }

        const designApiResponse = await designResponse.json();

        const designList =
          designApiResponse.success && designApiResponse.data
            ? designApiResponse.data
            : [];

        // console.log("Design Data:", designList);

        setDesignData(designList);
      } catch (err) {
        console.error("Fetch error:", err);
        setError("Failed to load content. Please check your connection.");
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, [preloadImages]);

  // Auto Banner Rotate
  useEffect(() => {
    if (bannerData.length === 0) return;

    const interval = setInterval(() => {
      setCurrentBannerIndex((prevIndex) =>
        prevIndex === bannerData.length - 1 ? 0 : prevIndex + 1,
      );
    }, 5000);

    return () => clearInterval(interval);
  }, [bannerData.length]);

  if (loading) {
    return (
      <div className="home-container">
        <div className="loading-screen">
          <div className="spinner-border"></div>
          <p>Loading designs & banners...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="home-container">
        <div className="error-screen">
          <i className="bi bi-wifi-off"></i>
          <h3>Connection Issue</h3>
          <p>{error}</p>
          <button
            className="retry-btn"
            onClick={() => window.location.reload()}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>
          Embroidery Designs | Machine Embroidery Designs | LifeTime Creation
        </title>

        <meta
          name="description"
          content="Explore premium and free machine embroidery designs for sarees, suits, kurtis, lehengas, blouses, neck designs, butta, butti, lace and more at LifeTime Creation."
        />

        <meta name="robots" content="index, follow" />

        <link rel="canonical" href="https://lifetimecreation.in/" />

        {/* Google / Search Sharing */}
        <meta
          property="og:title"
          content="Embroidery Designs | Machine Embroidery Designs | LifeTime Creation"
        />

        <meta
          property="og:description"
          content="Discover premium and free machine embroidery designs for sarees, suits, kurtis, lehengas, blouses and more."
        />

        <meta property="og:type" content="website" />

        <meta property="og:url" content="https://lifetimecreation.in" />

        <meta
          property="og:image"
          content="https://lifetimecreation.in/backend/about/intro/about.png"
        />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />

        <meta
          name="twitter:title"
          content="Embroidery Designs | LifeTime Creation"
        />

        <meta
          name="twitter:description"
          content="Premium and free machine embroidery designs for sarees, suits, kurtis, lehengas, blouses and more."
        />
      </Helmet>
      <div className="home-container">
        {/* Hero Banner Section */}
        <section className="hero-section">
          <div className="hero-slider">
            {bannerData.map((banner, index) => (
              <div
                key={index}
                className={`hero-slide ${
                  index === currentBannerIndex ? "active" : ""
                }`}
                style={{
                  backgroundImage: `url(${banner?.Value})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  backgroundRepeat: "no-repeat",
                }}
              >
                <div className="hero-overlay"></div>
              </div>
            ))}

            {/* Dots Indicator */}
            <div className="banner-indicators">
              {bannerData.map((_, index) => (
                <span
                  key={index}
                  className={`indicator ${
                    index === currentBannerIndex ? "active" : ""
                  }`}
                  onClick={() => setCurrentBannerIndex(index)}
                />
              ))}
            </div>
          </div>
        </section>

        {/* Design Section */}
        <section className="design-section">
          {designData.map((category) => (
            <div key={category.id} className="design-category">
              {/* Category Title */}
              <div className="section-header-row">
                <span className="line"></span>
                <span className="section-title1">{category.name}</span>
                <span className="line"></span>
              </div>

              {/* Subcategories */}
              <div className="design-grid">
                {category.subcategories.map((sub) => {
                  const design = sub.design?.[0];

                  // console.log("SUB:", sub);
                  // console.log("DESIGN:", design);

                  return (
                    <div
                      key={sub.id}
                      className="design-card"
                      style={{ cursor: "pointer" }}
                      onClick={() =>
                        navigate(
                          `/category?cid=${category.id}&menu=${encodeURIComponent(
                            category.name,
                          )}&subcid=${sub.id}&icid=${design?.icid || ""}`,
                        )
                      }
                    >
                      <div className="design-image">
                        <img
                          src={`https://lifetimedesign.in/controlpanel/Designs/${design?.image_url}`}
                          alt={sub.name}
                          onError={(e) => {
                            e.target.src = "/images/no-image.png";
                          }}
                        />
                      </div>

                      <div className="design-name">{sub.name}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </section>
        {/* {showCoursePopup && (
        <div className="course-popup">
          <button
            className="course-popup-close"
            onClick={() => setShowCoursePopup(false)}
          >
            <i className="bi bi-x-lg"></i>
          </button>

          <div className="popup-top">
            <i className="bi bi-lightning-charge-fill"></i>
            Career Decision Time
          </div>

          <h2>
            Do You Want To Become A{" "}
            <span>Professional Embroidery Designer?</span>
          </h2>
          <p className="popup-footer-text">
            If yes, this Embroidery Design course is designed for you.
          </p>

          <div className="popup-features">
            <div>
              <i className="bi bi-check-circle-fill"></i>
              Step-by-step Practical Training
            </div>

            <div>
              <i className="bi bi-check-circle-fill"></i>
              Real Industry Projects
            </div>

            <div>
              <i className="bi bi-check-circle-fill"></i>
              Freelancing & Job Guidance
            </div>

            <div>
              <i className="bi bi-check-circle-fill"></i>
              Lifetime Support
            </div>
          </div>

          <div className="popup-action">
            <button
              className="join-course-btn"
              onClick={() => {
                setShowCoursePopup(false);
                window.open("https://www.embroiderydesigncourse.com", "_blank");
              }}
            >
              <i className="bi bi-mortarboard-fill"></i>
              Yes, I Want To Join
            </button>
          </div>
        </div>
      )} */}
        {showCoursePopup && (
          <div className="course-popup">
            <button
              className="course-popup-close"
              onClick={() => setShowCoursePopup(false)}
            >
              <i className="bi bi-x-lg"></i>
            </button>

            <div className="popup-top">
              <i className="bi bi-lightning-charge-fill"></i>
              Our Services
            </div>

            <h2>
              <span style={{ fontSize: "20px" }}>Welcome to</span> <br />
              Lifetime Creation
            </h2>

            <hr />

            {/* ===== SERVICE 1 ===== */}
            <div className="ltc-popup-row">
              <div className="ltc-popup-col ltc-left">
                <h5>
                  Do You Want To Become A
                  <span> Professional Embroidery Designer?</span>
                </h5>

                <p className="ltc-popup-text">
                  Learn professional embroidery design skills with practical
                  training.
                </p>
                <a
                  href="https://wa.me/919909614445?text=Hello%20I%20want%20more%20information"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="whatsapp-btn"
                >
                  <i className="bi bi-whatsapp"></i>
                  Chat on WhatsApp
                </a>
              </div>

              <div className="ltc-popup-col ltc-right">
                <div className="ltc-img-wrapper">
                  <img
                    src="/images/marketing/offline.jpg"
                    alt="Course"
                    className="ltc-popup-side-img"
                  />

                  <div className="image-overlay1">
                    <button
                      className="join-course-btn"
                      onClick={() => {
                        setShowCoursePopup(false);
                        window.open(
                          "https://www.embroiderydesigncourse.com",
                          "_blank",
                        );
                      }}
                    >
                      <i className="bi bi-mortarboard-fill"></i>
                      Course
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <hr />

            {/* ===== SERVICE 2 ===== */}
            <div className="ltc-popup-row">
              <div className="ltc-popup-col ltc-left">
                <h5>
                  Need A <span>Website, App Or Software?</span>
                </h5>

                <p className="ltc-popup-text">
                  We build professional digital solutions for your business.
                </p>
                <a
                  href="https://wa.me/919909614445?text=Hello%20I%20want%20more%20information"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="whatsapp-btn"
                >
                  <i className="bi bi-whatsapp"></i>
                  Chat on WhatsApp
                </a>
              </div>

              <div className="ltc-popup-col ltc-right">
                <div className="ltc-img-wrapper">
                  <img
                    src="/images/marketing/lifetimeinfotech.png"
                    alt="Infotech"
                    className="ltc-popup-side-img"
                  />

                  <div className="image-overlay1">
                    <button
                      className="join-course-btn"
                      onClick={() => {
                        setShowCoursePopup(false);
                        window.open(
                          "https://www.lifetimeinfotech.in",
                          "_blank",
                        );
                      }}
                    >
                      <i className="bi bi-code-slash"></i>
                      Infotech
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <hr />

            {/* ===== SERVICE 3 ===== */}
            <div className="ltc-popup-row">
              <div className="ltc-popup-col ltc-left">
                <h5>
                  Free Designs & <span>Buy/Sell Machines</span>
                </h5>

                <p className="ltc-popup-text">
                  Explore embroidery marketplace & resources.
                </p>
              </div>

              <div className="ltc-popup-col ltc-right">
                <div className="ltc-img-wrapper">
                  <img
                    src="/images/marketing/embroideryBazar.png"
                    alt="Bazar"
                    className="ltc-popup-side-img"
                  />

                  <div className="image-overlay1">
                    <button
                      className="join-course-btn"
                      onClick={() => {
                        setShowCoursePopup(false);
                        window.open("https://www.embroiderybazar.in", "_blank");
                      }}
                    >
                      <i className="bi bi-shop"></i>
                      Visit Bazar
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <a href="/Service" className="service-link">
              <i className="bi bi-arrow-right-circle"></i>
              Go to Service Page
            </a>
          </div>
        )}
        {showWelcomePopup && (
          <div className="welcome-popup-overlay">
            <div className="welcome-popup">
              <button
                className="welcome-close"
                onClick={() => setShowWelcomePopup(false)}
              >
                <i className="bi bi-x-lg"></i>
              </button>

              <img
                src="/images/ads/offer.jpg"
                alt="Welcome"
                className="welcome-image"
              />

              <button
                className="welcome-btn"
                onClick={() => {
                  setShowWelcomePopup(false);
                  navigate("/balance");
                }}
              >
                Add Balance & Get Offer
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
